/**
 * Helpers for applying per-organization white-label branding at runtime.
 *
 * The app drives all theming through HSL CSS variables (see src/index.css).
 * We convert a brand hex color to the same `H S% L%` token format and inject
 * it on :root so `bg-primary`, `text-primary`, `ring`, etc. follow the brand.
 */

/** Curated fonts offered to org admins. Kept in sync with the backend allow-list. */
export const BRANDING_FONTS = [
  'Inter',
  'Roboto',
  'Lato',
  'Poppins',
  'Montserrat',
  'Open Sans',
  'Nunito',
  'Source Sans 3',
  'DM Sans',
  'Work Sans',
  'Raleway',
  'Rubik',
  'Manrope',
  'Playfair Display',
  'Merriweather',
  'Oswald',
  'Roboto Slab',
  'IBM Plex Mono',
] as const;

export type BrandingFont = (typeof BRANDING_FONTS)[number];

interface Hsl {
  h: number;
  s: number;
  l: number;
}

/** Parse `#rrggbb` → {h, s, l} (h in deg, s/l in %). Returns null on bad input. */
export function hexToHsl(hex: string): Hsl | null {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!match) return null;

  const int = parseInt(match[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return { h, s: Math.round(s * 100), l: Math.round(l * 100) };
}

/** CSS variable value (`H S% L%`) for a brand hex, or null if invalid. */
export function hexToHslVar(hex: string): string | null {
  const hsl = hexToHsl(hex);
  return hsl ? `${hsl.h} ${hsl.s}% ${hsl.l}%` : null;
}

/**
 * Foreground token (text drawn on top of the brand color). Picks white or the
 * app's near-black depending on the brand color's perceived luminance so labels
 * on `bg-primary` stay readable.
 */
export function foregroundForHex(hex: string): string {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!match) return '60 9.1% 97.8%'; // default light foreground
  const int = parseInt(match[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  // Perceived luminance (ITU-R BT.601).
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '20 14.3% 4.1%' : '60 9.1% 97.8%';
}

/** Read the live `--primary` CSS variable as HSL components, falling back to the
 * GenieFactory orange when unavailable (SSR / var unset). */
export function getBrandPrimaryHsl(): Hsl {
  const fallback: Hsl = { h: 24.6, s: 95, l: 53 };
  if (typeof document === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
  const m = /^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/.exec(raw);
  if (!m) return fallback;
  return { h: parseFloat(m[1]), s: parseFloat(m[2]), l: parseFloat(m[3]) };
}

/**
 * Read the org's secondary accent as HSL components, or `null` when no secondary
 * color is configured. Keyed on `--brand-secondary`, which the branding applier
 * sets *only* when an org picks a secondary color — unlike shadcn's `--secondary`
 * token (a neutral gray by default), so callers can cleanly fall back to primary.
 */
export function getBrandSecondaryHsl(): Hsl | null {
  if (typeof document === 'undefined') return null;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--brand-secondary').trim();
  const m = /^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/.exec(raw);
  if (!m) return null;
  return { h: parseFloat(m[1]), s: parseFloat(m[2]), l: parseFloat(m[3]) };
}

/** Build a chart palette of `count` shades from the brand colors by stepping
 * lightness down, so dashboards follow the organization's colors. With a
 * secondary accent set, the two brand hues alternate into a two-tone palette;
 * otherwise it stays the original single-hue ramp. */
export function brandChartPalette(count: number): string[] {
  const primary = getBrandPrimaryHsl();
  const secondary = getBrandSecondaryHsl();
  const steps = [0, -8, -13, -18, -23, -28, -33, -38];
  if (!secondary) {
    return Array.from({ length: count }, (_, i) => {
      const ll = Math.max(15, Math.min(85, primary.l + steps[i % steps.length]));
      return `hsl(${primary.h} ${primary.s}% ${ll}%)`;
    });
  }
  return Array.from({ length: count }, (_, i) => {
    const base = i % 2 === 0 ? primary : secondary;
    const ll = Math.max(15, Math.min(85, base.l + steps[Math.floor(i / 2) % steps.length]));
    return `hsl(${base.h} ${base.s}% ${ll}%)`;
  });
}
